import ExportFiscalMensuel from '../components/shared/ExportFiscalMensuel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Printer, Search, Download } from '../lib/icons';
import { toast } from 'sonner';
import { fetchFacturier } from '../lib/dataProvider';
import { TYPES_DOC } from '../lib/souches';
import { fetchEmissions } from '../lib/emission';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { PrintDoc } from '../components/print/PrintDoc';
import { TableauFacturable } from '../components/facturation/TableauFacturable';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const STATUTS = ['Tous', 'Payé', 'Créance', 'En attente'];
const COULEURS = {
  Payé: 'bg-emerald-50 text-emerald-800',
  Créance: 'bg-amber-50 text-amber-800',
  'En attente': 'bg-slate-100 text-slate-600',
};

const ONGLETS = [
  ['facturable', 'Opérations facturables'],
  ['documents', 'Documents & bons émis'],
];

export default function Facturier() {
  const [onglet, setOnglet] = useState('facturable');
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [statut, setStatut] = useState('Tous');
  const [source, setSource] = useState('Toutes');
  const [doc, setDoc] = useState(null);

  const load = useCallback(() => {
    // Les numéros officiels viennent du serveur (souche unique) : le facturier
    // affiche « non émis » tant qu'aucune facture officielle n'a été demandée.
    Promise.all([fetchFacturier(), fetchEmissions()]).then(([lignes, emissions]) => {
      const parOperation = new Map(emissions.filter((e) => e.type_document === 'facture').map((e) => [`${e.operation_type}:${e.operation_id}`, e]));
      setRows(
        lignes.map((r) => {
          const officielle = r.operationType ? parOperation.get(`${r.operationType}:${r.ref}`) : null;
          return officielle ? { ...r, numero: officielle.numero, officielle } : r;
        })
      );
    });
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const sources = useMemo(() => ['Toutes', ...new Set(rows.map((r) => r.source))], [rows]);

  const visibles = rows.filter((r) => {
    const t = q.trim().toLowerCase();
    const okQ =
      !t ||
      [r.tiers, r.numero, r.ref, r.categorie].some((v) => String(v ?? '').toLowerCase().includes(t));
    return okQ && (statut === 'Tous' || r.statut === statut) && (source === 'Toutes' || r.source === source);
  });

  const totalVisible = visibles.reduce((s, r) => s + r.montant, 0);
  const creances = rows.filter((r) => r.statut === 'Créance').reduce((s, r) => s + r.montant, 0);
  const attente = rows.filter((r) => r.statut === 'En attente').reduce((s, r) => s + r.montant, 0);

  const imprimer = (r) => {
    // Consulter ou imprimer n'émet JAMAIS de numéro officiel : on affiche le
    // numéro interne déjà attribué, ou la référence de l'opération.
    setDoc({ ...r, numero: r.numero ?? r.ref });
  };

  const exportCsv = () => {
    const lignes = [
      'Numero;Reference;Date;Tiers;Categorie;Source;Montant (TND);Statut',
      ...visibles.map((r) =>
        [r.numero ?? '', r.ref, r.date, r.tiers, r.categorie, r.source, r.montant.toFixed(3), r.statut].join(';')
      ),
    ];
    const url = URL.createObjectURL(new Blob([lignes.join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'facturier.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Facturier exporté');
  };

  return (
    <div data-testid="facturier-page" className="space-y-6">
      <PageTitle
        emoji="🗂️"
        titre="Facturation & Documents"
        sous="Tous les bons et factures émis par l'huilerie, quel que soit le module d'origine"
        action={
          <button
            type="button"
            data-testid="facturier-export"
            onClick={exportCsv}
            className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10"
          >
            <Download size={15} /> Export CSV
          </button>
        }
      />

      <ExportFiscalMensuel type="ventes" />

      <div className="flex flex-wrap gap-px bg-slate-200">
        {ONGLETS.map(([id, libelle]) => (
          <button
            key={id}
            type="button"
            data-testid={`onglet-${id}`}
            onClick={() => setOnglet(id)}
            className={`px-4 py-3 font-heading text-sm font-bold uppercase tracking-[0.06em] transition-colors duration-150 ${
              onglet === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {libelle}
          </button>
        ))}
      </div>

      {onglet === 'facturable' && <TableauFacturable />}

      {onglet === 'documents' && (
      <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <StatBlock testid="stat-docs" titre="Documents émis" accent="border-l-slate-900" items={[['Total', fmt(rows.length)], ['Affichés', fmt(visibles.length)]]} />
        <StatBlock testid="stat-creances-docs" titre="Créances clients" accent="border-l-amber-500" items={[['Montant (TND)', fmt(creances, 3)]]} />
        <StatBlock testid="stat-attente-docs" titre="En attente de règlement" accent="border-l-sky-600" items={[['Montant (TND)', fmt(attente, 3)]]} />
      </div>

      <div className="flex flex-col gap-3 border border-slate-200 bg-white px-4 py-3 lg:flex-row lg:items-end">
        <div className="flex-1">
          <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Recherche</label>
          <div className="mt-1.5 flex h-11 items-center gap-2 border border-slate-300 bg-slate-50 px-3 focus-within:border-slate-900 focus-within:bg-white">
            <Search size={14} className="text-slate-400" />
            <input
              data-testid="facturier-recherche"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom du contact, n° de document, catégorie…"
              className="h-full w-full bg-transparent font-data text-sm outline-none"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Statut</label>
          <div className="mt-1.5 flex flex-wrap gap-px bg-slate-200">
            {STATUTS.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`filtre-statut-${s.toLowerCase().replace(' ', '-')}`}
                onClick={() => setStatut(s)}
                className={`px-3 py-2.5 text-sm font-bold transition-colors duration-150 ${
                  statut === s ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Module d'origine</label>
          <select
            data-testid="filtre-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white lg:w-52"
          >
            {sources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-facturier">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['N° document', 'Réf. interne', 'Date', 'Client / Fournisseur', 'Catégorie', 'Origine', 'Montant (TND)', 'Statut', 'Actions'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 ? (
                <tr data-testid="facturier-empty">
                  <td colSpan={9} className="px-3 py-12 text-center text-sm text-slate-500">
                    Aucun document ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                visibles.map((r) => (
                  <tr key={r.cle} data-testid={`facturier-row-${r.ref}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">
                      {r.numero ?? <span className="text-slate-400">non émis</span>}
                    </td>
                    <td className="px-3 py-2 font-data text-xs text-slate-500">{r.ref}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{r.date}</td>
                    <td className="px-3 py-2 text-sm font-semibold text-slate-900">{r.tiers}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{r.categorie}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-500">{r.source}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(r.montant, 3)}</td>
                    <td className="px-3 py-2">
                      <span data-testid={`statut-${r.ref}`} className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${COULEURS[r.statut]}`}>
                        {r.statut}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        data-testid={`imprimer-${r.ref}`}
                        onClick={() => imprimer(r)}
                        className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        <Printer size={13} /> PDF
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-500">{visibles.length} document(s) affiché(s)</p>
          <p data-testid="facturier-total" className="font-data text-sm font-semibold text-slate-900">
            Total affiché : {fmt(totalVisible, 3)} TND
          </p>
        </div>
      </section>
      </>
      )}

      {doc && (
        <PrintDoc
          type={TYPES_DOC.find((t) => t.id === doc.typeDoc)?.label ?? 'Document'}
          numero={doc.numero}
          emission={
            doc.operationType
              ? {
                  type: doc.operationType,
                  id: doc.ref,
                  libelle: doc.categorie,
                  date: doc.date,
                  tiersId: doc.tiersId,
                  tiers: doc.tiers,
                  referenceInterne: doc.ref,
                  total: doc.montant,
                  lignes: doc.lignes,
                  totaux: [['Montant total', `${fmt(doc.montant, 3)} TND`]],
                  onEmis: load,
                }
              : undefined
          }
          date={doc.date}
          tiers={doc.tiers}
          lignes={[['Catégorie', doc.categorie], ['Référence interne', doc.ref], ...doc.lignes]}
          totaux={[['Montant total', `${fmt(doc.montant, 3)} TND`]]}
          mentions={`Statut : ${doc.statut}. Document émis par ${doc.source}. Montants en dinars tunisiens (TND).`}
          onClose={() => {
            setDoc(null);
            load();
          }}
        />
      )}
    </div>
  );
}
